import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { ArrowLeft, KeyRound, Users, Loader2 } from 'lucide-react';
import axios from 'axios';
import { UserHeader } from '../../components/UserHeader';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function CampusUnirse() {
  const [searchParams] = useSearchParams();
  const codigoParam = searchParams.get('codigo') || '';
  const [codigo, setCodigo] = useState(codigoParam);
  const [loading, setLoading] = useState(false);
  const [infoEspacio, setInfoEspacio] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  // Auto-search if code comes from URL
  useEffect(() => {
    if (codigoParam) {
      buscarEspacio(codigoParam);
    }
  }, []);

  const buscarEspacio = async (cod) => {
    const c = (cod || codigo).trim();
    if (!c) return;
    setError('');
    setInfoEspacio(null);
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/campus/invitacion/${c}/info`);
      setInfoEspacio(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Código inválido');
    } finally {
      setLoading(false);
    }
  };

  const unirse = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(
        `${API_URL}/campus/invitacion/${codigo.trim()}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess(res.data.message);
      setTimeout(() => {
        navigate(`/campus/espacio/${res.data.space_id}`);
      }, 1200);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al unirse');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <UserHeader />
      <div className="py-8 px-4">
      <div className="max-w-md mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate('/campus')}
          className="mb-4"
          data-testid="back-to-campus-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Volver al Campus
        </Button>

        <Card>
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-violet-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <KeyRound className="w-8 h-8 text-violet-600" />
            </div>
            <CardTitle>Unirse a un espacio</CardTitle>
            <p className="text-sm text-gray-500 mt-1">
              Ingresá el código de invitación que te compartieron
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Input
                placeholder="Ej: ABC123"
                value={codigo}
                onChange={(e) => setCodigo(e.target.value.toUpperCase())}
                className="text-center text-lg tracking-widest font-mono"
                maxLength={8}
                data-testid="invitation-code-input"
                onKeyDown={(e) => e.key === 'Enter' && buscarEspacio()}
              />
              <Button
                onClick={() => buscarEspacio()}
                disabled={!codigo.trim() || loading}
                data-testid="search-space-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Buscar'}
              </Button>
            </div>

            {error && (
              <div className="text-sm text-red-600 bg-red-50 p-3 rounded-md" data-testid="join-error-msg">
                {error}
              </div>
            )}

            {success && (
              <div className="text-sm text-green-700 bg-green-50 p-3 rounded-md" data-testid="join-success-msg">
                {success}
              </div>
            )}

            {infoEspacio && !success && (
              <div className="border rounded-lg p-4 space-y-3" data-testid="space-info-card">
                <div className="flex items-center gap-3">
                  <div
                    className="w-12 h-12 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: infoEspacio.color_tema || '#7c3aed' }}
                  >
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold">{infoEspacio.nombre}</h3>
                    <p className="text-xs text-gray-500">
                      {infoEspacio.tipo === 'curso' ? 'Curso' : 'Comisión'} · {infoEspacio.total_miembros} miembros
                    </p>
                  </div>
                </div>
                {infoEspacio.descripcion && (
                  <p className="text-sm text-gray-600">{infoEspacio.descripcion}</p>
                )}
                <Button
                  className="w-full"
                  onClick={unirse}
                  disabled={loading}
                  data-testid="join-space-btn"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Unirme a este espacio
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
    </div>
  );
}
